import uuid from 'uuid';

import JsonApi, {getBaseDomainUrl} from "../../api/jsonApi";
import {getCommonVisualizerType} from "../../config";
import {setMultiLoader} from "../../actions/actionFunctions";
import {createThumbnailsCanvas, hideGroupObject, toBase64} from "./aiFunctions";
import {
  setAILoading,
  setAIResults, setAIWaitingId,
  setLookAiImageOpened,
  setLookAiRenderFinished,
  setLookAiWaitingBoxClicked
} from "./aiActions";
import {LOOK_OVERLAYS} from "../look/core/constants";
import {getImagePromiseFromLayout} from "../../misc/getCanvasImageFromLayout";
import {getCurrentLayout} from "../appLayout";
import {convertImgToBase64URLNoCors} from "../../misc/utils";
import {playAudio} from "../../misc/audioCache";
import {lowerCaseFirstLetterKeys} from "../../misc/misc2";
import {attachResumeListenerOnce, detachResumeListener} from "./aiListenersLogic";

const newSwaggerHeaders = {
  'Accept': '*',
  'Content-Type': 'application/json'
}

const successSoundUrl = "https://qc-cdn.s3.dualstack.us-east-1.amazonaws.com/sounds/finish_tiered_chime.wav";

export const getCurrentPrompts = async () => {
  const rawResponse = await fetch(`${getBaseDomainUrl()}:7122/api/Prompt`);
  const convertResponse = await rawResponse.json();
  console.log("convertResponse", convertResponse);
  if (!Array.isArray(convertResponse)) {
    return []
  }
  const visualizerType = getCommonVisualizerType();
  const filteredResponse = convertResponse.filter(prObject => prObject.tool === visualizerType);
  let firstActiveIndex = filteredResponse.findIndex(prObject => prObject.isDefault && (prObject.type === "First"));
  let secondActiveIndex = filteredResponse.findIndex(prObject => prObject.isDefault && (prObject.type === "Second"));
  let thirdActiveIndex = filteredResponse.findIndex(prObject => prObject.isDefault && (prObject.type == "Third"));
  if (firstActiveIndex === -1) {
    firstActiveIndex = filteredResponse.findIndex(prObject => prObject.type === "First"); // set at least something active
  }
  if (secondActiveIndex === -1) {
    secondActiveIndex = filteredResponse.findIndex(prObject => prObject.type === "Second"); // set at least something active
  }
  if (thirdActiveIndex === -1) {
    thirdActiveIndex = filteredResponse.findIndex(prObject => prObject.type === "Third"); // set at least something active
  }
  if (firstActiveIndex > -1) {
    filteredResponse[firstActiveIndex]["isActiveFirst"] = true;
  }
  if (secondActiveIndex > -1) {
    filteredResponse[secondActiveIndex]["isActiveSecond"] = true;
  }
  if (thirdActiveIndex > -1) {
    filteredResponse[thirdActiveIndex]["isActiveThird"] = true;
  }
  return filteredResponse
}

export const removePrompt = (pr) => {
  return fetch(`${getBaseDomainUrl()}:7122/api/Prompt/${pr.id}`, {
    method: 'DELETE',
  });
}

export const editPrompt = (pr) => {
  return fetch(`${getBaseDomainUrl()}:7122/api/Prompt`, {
    method: 'PATCH',
    headers: newSwaggerHeaders,
    body: JSON.stringify(pr)
  });
}

export const createPrompt = (postData) => {
  return fetch(`${getBaseDomainUrl()}:7122/api/Prompt`, {
    method: 'POST',
    headers: newSwaggerHeaders,
    body: JSON.stringify(postData)
  });
}

export const setDefaultPrompts = async (activePromptFirst, activePromptSecond, activePromptThird) => {
  if (activePromptFirst) {
    await fetch(`${getBaseDomainUrl()}:7122/api/Prompt/default/${activePromptFirst.id}`, {
      method: 'PATCH',
      headers: newSwaggerHeaders,
    })
  }
  if (activePromptSecond) {
    await fetch(`${getBaseDomainUrl()}:7122/api/Prompt/default/${activePromptSecond.id}`, {
      method: 'PATCH',
      headers: newSwaggerHeaders,
    })
  }
  if (activePromptThird) {
    await fetch(`${getBaseDomainUrl()}:7122/api/Prompt/default/${activePromptThird.id}`, {
      method: 'PATCH',
      headers: newSwaggerHeaders,
    })
  }
}

const fixAiResponse = (obj) => {
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
    return obj;
  }
  const newObj = lowerCaseFirstLetterKeys(obj);
  if (Array.isArray(newObj.files) && newObj.files.length) {
    newObj.files = newObj.files.map(lowerCaseFirstLetterKeys);
  }
  return newObj
}

const addCanvasImageToPayload = async (onlyRenderGroups = null) => {
  const imageData = await getImagePromiseFromLayout(
    getCurrentLayout(),
    {width: 1920, height: 1080},
    1,
    false,
    onlyRenderGroups // onlyRenderGroups.map(fg => fg.featureGroup) // no need for that as we are using only partId
  );
  return {
    "b64File": imageData.split(',')[1],
    "fileName": "reference.jpg"
  }
}

export const postGenerateAiImage = (popupContext, signal) => {
  return async (dispatch, getState) => {
    const {groupObjects, userFile, promptsList, selectedChatModel, customChatModel, selectedImageModel,
      customImageModel} = popupContext;
    const storeState = getState();
    const {aiResults, aiRenderOptions} = storeState.aiState;
    const {aiEnableReferenceImage, aiEnableThumbnailImages, aiSendOnlySelectedGroupsAsAReference,
      aiSendCombinedThumbnails} = aiRenderOptions;

    dispatch(setAILoading(true));
    dispatch(setMultiLoader({ activeStep: 0, visible: true, finished: false }));

    const onlySelectedGroups = groupObjects.filter(groupObject => {
      return groupObject.enabled && !hideGroupObject(groupObject) && groupObject.featureGroup?.activeFeature?.dbFeature?.featureDefinitionID
    });
    const featureDefinitionIDs = onlySelectedGroups.map(groupObject => {
      return groupObject.featureGroup.activeFeature.dbFeature.featureDefinitionID
    });

    const base64Code = await toBase64(userFile);

    const activePromptFirst = promptsList.find(promptObject => promptObject.isActiveFirst);
    const activePromptSecond = promptsList.find(promptObject => promptObject.isActiveSecond);
    const activePromptThird = promptsList.find(promptObject => promptObject.isActiveThird);
    if (!activePromptFirst) {
      return
    }

    await setDefaultPrompts(activePromptFirst, activePromptSecond, activePromptThird);
    const jobId = document.kvConfig.jobId || storeState.serverState.jobId;

    const postData = {
      "fabricatorId": document.kvConfig.userId,
      "featureDefinitionIds": featureDefinitionIDs,
      "jobId": jobId,
      "userPhoto": {
        "b64File": base64Code,
        "fileName": userFile.name,
      },
      "tool": getCommonVisualizerType(),
      "firstPromptVersion": activePromptFirst.version,
      "secondPromptVersion": activePromptSecond ? activePromptSecond.version : 0,
      "thirdPromptVersion": activePromptThird ? activePromptThird.version : 0,
      "openAI_CustomChatModel": selectedChatModel === "custom" ? customChatModel : selectedChatModel,
      "openAI_CustomImageModel": selectedImageModel === "custom" ? customImageModel: selectedImageModel,
      "skipImages": !aiEnableThumbnailImages,
    };

    if (aiEnableReferenceImage) {
      const onlyGroups = aiSendOnlySelectedGroupsAsAReference ? onlySelectedGroups : null;
      postData.examplePhoto = await addCanvasImageToPayload(onlyGroups);
    }
    if (aiSendCombinedThumbnails) {
      postData.productGridImage =  await createThumbnailsCanvas(onlySelectedGroups, aiSendOnlySelectedGroupsAsAReference);
    }

    console.log("postData", postData);
    try {
      const rawResponse = await fetch(`${getBaseDomainUrl()}:7122/api/ImageProcessing/convert`, {
        method: 'POST',
        headers: newSwaggerHeaders,
        body: JSON.stringify(postData),
        signal,
      });
      const convertResponse = await rawResponse.json();
      await playAudio(successSoundUrl);
      dispatch(setAILoading(false));
      dispatch(setAIResults([...aiResults, fixAiResponse(convertResponse)]));
      console.log("convertResponse", convertResponse);
      return convertResponse ? convertResponse.resultImageUrl : "";
    } catch (e) {
      dispatch(setAILoading(false));
      return "";
    }
  }
}

const _getFeatureGroupsWithEnabledThumbnails = async () => {
  const rawResponse = await fetch(`${getBaseDomainUrl()}:7122/api/Prompt`);
  const convertResponse = await rawResponse.json();
  if (!Array.isArray(convertResponse)) {
    return []
  }
  const visualizerType = getCommonVisualizerType();
  const filteredResponse = convertResponse.filter(prObject => prObject.tool === visualizerType);
  let firstActiveIndex = filteredResponse.find(prObject => prObject.isDefault && (prObject.type === "First"));
  let secondActiveIndex = filteredResponse.find(prObject => prObject.isDefault && (prObject.type === "Second"));
  let thirdActiveIndex = filteredResponse.find(prObject => prObject.isDefault && (prObject.type === "Third"));
  const activePrompts = [firstActiveIndex, secondActiveIndex, thirdActiveIndex].filter(prObject => {
    return prObject && (prObject.id || (typeof prObject.id === "number"))
  });
  if (!activePrompts.length) {
    return []
  }
  const allResponses = await Promise.all(
    activePrompts.map((prObject) => getCurrentPromptParts(prObject))
  );
  const layout = getCurrentLayout();
  const groupsWithEnabledThumbnails = allResponses.reduce((total, prPartArray) => {
    if (!Array.isArray(prPartArray) || !prPartArray.length) {
      return total;
    }
    prPartArray.forEach(partObject => {
      if (partObject.sendThumbnail && !total.some(fg => (fg.dbName || fg.partName) === partObject.featureGroup)) {
        const fg = layout.getFeatureGroupByGroupName(partObject.featureGroup);
        if (fg) {
          total.push(fg);
        }
      }
    });
    return total;
  }, []);
  // console.log("groupsWithEnabledThumbnails", groupsWithEnabledThumbnails);
  return groupsWithEnabledThumbnails
}

const _preparePostData = async (lookAiPopupContext, storeState, dispatch) => {
  const {userFile, groupObjects} = lookAiPopupContext;
  const {serverState, aiState} = storeState;

  const {aiEnableReferenceImage, aiEnableThumbnailImages, aiSendOnlySelectedGroupsAsAReference,
    aiSendCombinedThumbnails} = aiState.aiRenderOptions;

  dispatch(setAILoading(true));

  if (serverState.isMobile) {
    const scrollDiv = document.querySelector(".look-ai__content");
    const loadingDiv = document.querySelector(".look-ai__sidebar-content--dropzone");
    if (loadingDiv && scrollDiv?.scrollTop > 120) {
      loadingDiv.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  const onlySelectedGroups = groupObjects.filter(groupObject => groupObject.enabled && !hideGroupObject(groupObject));
  const featureDefinitionIDs = onlySelectedGroups.map(groupObject => {
    return groupObject.featureGroup.activeFeature.dbFeature.featureDefinitionID
  });

  const base64Code = await toBase64(userFile);

  const postData = {
    "fabricatorId": document.kvConfig.userId,
    "featureDefinitionIds": featureDefinitionIDs,
    "jobId": document.kvConfig.jobId || serverState.jobId,
    "userPhoto": {
      "b64File": base64Code,
      "fileName": userFile.name,
    },
    "tool": getCommonVisualizerType(),
    "skipImages": !aiEnableThumbnailImages,
    // "firstPromptVersion": 0, // do not pass - we should use default ones
    // "secondPromptVersion": 0 // do not pass - we should use default ones
  };
  if (aiEnableReferenceImage) {
    const onlyGroups = aiSendOnlySelectedGroupsAsAReference ? onlySelectedGroups : null;
    postData.examplePhoto = await addCanvasImageToPayload(onlyGroups);
  }
  if (aiSendCombinedThumbnails) {
    try {
      const featureGroupsWithEnabledThumbnails = await _getFeatureGroupsWithEnabledThumbnails();
      if (Array.isArray(featureGroupsWithEnabledThumbnails) && featureGroupsWithEnabledThumbnails.length) {
        postData.productGridImage =  await createThumbnailsCanvas(
          onlySelectedGroups,
          aiSendOnlySelectedGroupsAsAReference,
          featureGroupsWithEnabledThumbnails
        );
      } else {
        postData.productGridImage =  await createThumbnailsCanvas(onlySelectedGroups, aiSendOnlySelectedGroupsAsAReference);
      }
    } catch (e) {
      postData.productGridImage =  await createThumbnailsCanvas(onlySelectedGroups, aiSendOnlySelectedGroupsAsAReference);
    }
  }
  return postData
}

const _parseImageResponse = async (convertResponse, dispatch, getState) => {

  detachResumeListener?.();

  const storeState = getState();
  const {aiResults, aiUserFileBlobUrl} = storeState.aiState;
  const {lookOverlayOpened} = storeState.lookState;

  const nextAiResults = aiResults.map(aiResult => {
    return Object.assign({}, aiResult, {isActive: false});
  })

  convertResponse.id = uuid.v4();
  convertResponse.isActive = true;
  convertResponse.sourceImageUrl = aiUserFileBlobUrl;

  await playAudio(successSoundUrl);

  dispatch(setAILoading(false));
  dispatch(setLookAiWaitingBoxClicked(true));
  if (nextAiResults.every(res => res.resultImageUrl)) {
    dispatch(setAIResults([...nextAiResults, convertResponse]));
  } else {
    const nextUnrenderedIndex = nextAiResults.findIndex(res => !res.resultImageUrl);
    if (nextUnrenderedIndex > -1) {
      nextAiResults[nextUnrenderedIndex] = convertResponse;
      dispatch(setAIResults([...nextAiResults]));
    }
  }

  if (lookOverlayOpened === LOOK_OVERLAYS.aiPanel) {
    dispatch(setLookAiImageOpened(true))
  } else {
    dispatch(setLookAiRenderFinished(true))
  }

  console.log("convertResponse", convertResponse);
  return convertResponse ? convertResponse.resultImageUrl : "";
}

const _parseErrorResponse = async (errOrResponse, dispatch, getState) => {

  detachResumeListener?.();

  let message = "Failed to generate image. Please try again later";

  // HTTP error case
  if (errOrResponse && typeof errOrResponse === "object" && "ok" in errOrResponse && "status" in errOrResponse) {
    try {
      const contentType = errOrResponse.headers?.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const data = await errOrResponse.json();
        message = data?.message || JSON.stringify(data);
      } else {
        message = (await errOrResponse.text()) || message;
      }
    } catch {
      // keep fallback message
    }
    message = `HTTP ${errOrResponse.status}: ${message}`;
  } else {
    // thrown error case
    message = errOrResponse?.message || String(errOrResponse) || message;
    if (message === "signal is aborted without reason") message = "Aborted manually";
  }

  const storeState = getState();
  const { aiResults } = storeState.aiState;

  const nextAiResults = aiResults.map(aiResult => ({ ...aiResult, isActive: false }));

  const convertResponse = {
    id: uuid.v4(),
    isActive: true,
    conversionError: message,
  };

  if (nextAiResults.every(res => res.resultImageUrl)) {
    dispatch(setAIResults([...nextAiResults, convertResponse]));
  } else {
    const idx = nextAiResults.findIndex(res => !res.resultImageUrl);
    if (idx > -1) {
      nextAiResults[idx] = convertResponse;
      dispatch(setAIResults([...nextAiResults]));
    }
  }

  dispatch(setAILoading(false));
  dispatch(setLookAiImageOpened(false));
  dispatch(setLookAiWaitingBoxClicked(true));
};

export const postGenerateLookAiImage = (lookAiPopupContext, signal) => {

  return startAIImageGeneration(lookAiPopupContext, signal)

  // return async (dispatch, getState) => {
  //
  //   const postData = await _preparePostData(lookAiPopupContext, getState(), dispatch);
  //
  //   console.log("postData", postData);
  //   try {
  //     const rawResponse = await fetch(`${getBaseDomainUrl()}:7122/api/ImageProcessing/convert`, {
  //       method: 'POST',
  //       headers: newSwaggerHeaders,
  //       body: JSON.stringify(postData),
  //       signal,
  //     });
  //
  //     if (!rawResponse.ok) {
  //       await _parseErrorResponse(rawResponse, dispatch, getState); // ✅ centralized
  //       return "";
  //     }
  //     const convertResponse = await rawResponse.json();
  //     return await _parseImageResponse(convertResponse, dispatch, getState);
  //   } catch (e) {
  //     // await new Promise(resolve => setTimeout(resolve, 5000)); // devtest // need to change api url to get here
  //     await _parseErrorResponse(e, dispatch, getState);
  //     return "";
  //   }
  // }
}

// const sendImageToOpenAI = async (base64Image) => {
//   try {
//     // Convert base64 to blob
//     const byteString = atob(base64Image.split(',')[1]);
//     const mimeString = base64Image.split(',')[0].split(':')[1].split(';')[0];
//     const ab = new ArrayBuffer(byteString.length);
//     const ia = new Uint8Array(ab);
//     for (let i = 0; i < byteString.length; i++) {
//       ia[i] = byteString.charCodeAt(i);
//     }
//     const blob = new Blob([ab], { type: mimeString });
//
//     const file = new File([blob], 'image.png', { type: mimeString });
//
//     // Create form data
//     const formData = new FormData();
//     formData.append('image', file);
//     formData.append('n', '1'); // number of images to return
//     formData.append('size', '512x512'); // size can be 256x256, 512x512, 1024x1024
//
//     // Send to OpenAI API (proxy this through your backend in production!)
//     const response = await axios.post(
//       'https://api.openai.com/v1/images/variations',
//       formData,
//       {
//         headers: {
//           // 'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
//           'Content-Type': 'multipart/form-data',
//         },
//       }
//     );
//
//     return response.data.data[0].url; // Returns a URL to the new image
//   } catch (error) {
//     console.error('Error sending image to OpenAI:', error);
//     throw error;
//   }
// };

export const getCurrentPromptParts = async (promptObject) => {
  const rawResponse = await fetch(`${getBaseDomainUrl()}:7122/api/PromptParts/prompt/${promptObject.id}`);
  const convertResponse = await rawResponse.json();
  console.log("promptPartsResponse", convertResponse);
  if (!Array.isArray(convertResponse)) {
    return []
  }
  // return the whole array, removed filter by partBody text, since we need other values
  return convertResponse
}

export const updatePromptParts = (groupObject, promptObject, type) => {
  const isActiveBody = "partBody" + type;
  const savedPromptPartObject = "savedPromptPartObject" + type;

  // console.log("groupObject", groupObject, promptObject, type);
  if (groupObject[savedPromptPartObject]) { // update
    return fetch(`${getBaseDomainUrl()}:7122/api/PromptParts/${groupObject[savedPromptPartObject].id}`, {
      method: 'PUT',
      headers: newSwaggerHeaders,
      body: JSON.stringify(Object.assign({}, groupObject[savedPromptPartObject], {
        partBody: groupObject[isActiveBody],
        sendThumbnail: groupObject["sendThumbnail"],
      }))
    });
  } else { // create
    return fetch(`${getBaseDomainUrl()}:7122/api/PromptParts/list`, {
      method: 'POST',
      headers: newSwaggerHeaders,
      body: JSON.stringify([{
        "id": 0,
        "promptId": promptObject.id,
        "featureGroup": groupObject.featureGroup.dbName || groupObject.featureGroup.partName,
        "partBody": groupObject[isActiveBody] || "",
        "sendThumbnail": groupObject.sendThumbnail,
      }])
    });
  }
}

export const createPromptsForNewPartObject = async (newPromptObject, oldPromptObject) => {

  const prPartsResponse = await getCurrentPromptParts(oldPromptObject);

  const allResponses = await Promise.all(prPartsResponse.map((prPart) => {
    return fetch(`${getBaseDomainUrl()}:7122/api/PromptParts/list`, {
      method: 'POST',
      headers: newSwaggerHeaders,
      body: JSON.stringify([{
        "id": 0,
        "promptId": newPromptObject.id,
        "featureGroup": prPart.featureGroup,
        "partBody": prPart.partBody,
        "sendThumbnail": prPart.sendThumbnail,
      }])
    })
  }))
  console.log("allResponses", allResponses);
}

export const saveCreatedAIImage = async (jobId, aiState) => {
  try {
    if (jobId && aiState.aiResults[0] && !aiState.aiResults[0].conversionError && aiState.aiResults[0].resultImageUrl) {
      const sourceImageUrl = await convertImgToBase64URLNoCors(aiState.aiResults[0].sourceImageUrl);
      const resultImageUrl = await convertImgToBase64URLNoCors(aiState.aiResults[0].resultImageUrl);
      let postData = [];
      if (sourceImageUrl) { // just in case
        postData.push({
          "fileName": "user_photo.png",
          "base64Content": sourceImageUrl.split(',')[1],
          "documentType": "user_room_photo",
          "jobId": jobId
        })
      }
      if (resultImageUrl) { // just in case
        postData.push({
          "fileName": "generated_photo.png",
          "base64Content": resultImageUrl, // s3 link!
          "documentType": "ai_room_photo",
          "jobId": jobId
        })
      }
      await JsonApi.postSaveJobDocument(postData);
      // dispatch(setAIResults(aiState.aiResults.map((aiRes, aiResIndex) => {
      //   if (aiResIndex === 0) {
      //     return Object.assign({}, aiRes, {isSavedOnBackend: true});
      //   }
      //   return aiRes
      // })))
    }
  } catch (error) {
    console.log("saveCreatedAIImage error", error);
  }
  return Promise.resolve();
}

const POLL_INTERVAL_MS = 10000;

const delay = (ms, signal) => {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);

    if (!signal) return;

    const onAbort = () => {
      clearTimeout(t);
      reject(new DOMException("Aborted", "AbortError"));
    };

    if (signal.aborted) return onAbort();

    signal.addEventListener("abort", onAbort, { once: true });
  });
}

// Guards against duplicate polling/finalization of the same process.
// The primary loop (startAIImageGeneration) and the resume listener
// (attachResumeListenerOnce, fired on window focus/visibility change) can both
// poll the same processId concurrently. Without these guards, both loops would
// see status "Finished" and each dispatch setAIResults — producing two images.
const activePollProcessIds = new Set(); // processIds currently being polled
let finalizedProcessId = null;          // last processId that produced a result/error

export const pollProgressUntilDone = async ({
                                        aiWaitingId,
                                        signal,
                                        dispatch,
                                        getState,
                                      }) => {
  // If another loop is already polling this process, do not start a second one.
  if (activePollProcessIds.has(aiWaitingId)) return "";
  activePollProcessIds.add(aiWaitingId);

  try {
    while (true) {
      // stop immediately if user aborted
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

      const raw = await fetch(
        `${getBaseDomainUrl()}:7122/api/ImageProcessing/progress/${aiWaitingId}`,
        { method: "GET", headers: newSwaggerHeaders, signal }
      );

      if (!raw.ok) {
        // HTTP error while polling
        if (finalizedProcessId === aiWaitingId) return "";
        finalizedProcessId = aiWaitingId;
        await _parseErrorResponse(raw, dispatch, getState);
        return "";
      }

      const progress = await raw.json();
      const status = progress?.status ?? progress?.Status;

      // If your API returns numeric enum, you might need mapping:
      // 0 Started, 1 InProgress, 2 Failed, 3 Finished, 4 Interupted

      if ((status === "Finished" || status === 3) && progress.response) {
        // Finalize exactly once per process (check+set is synchronous, so even
        // concurrent loops can only pass here a single time).
        if (finalizedProcessId === aiWaitingId) return "";
        finalizedProcessId = aiWaitingId;
        return await _parseImageResponse(fixAiResponse(progress.response), dispatch, getState);
      }

      if (status === "Failed" || status === 2 || status === "Interupted" || status === 4) {
        if (finalizedProcessId === aiWaitingId) return "";
        finalizedProcessId = aiWaitingId;
        // Build an Error-like object for your error handler
        const msg =
          progress?.response?.error ||
          progress?.message ||
          progress?.error ||
          progress?.conversionError ||
          `AI processing ${status === 4 || status === "Interupted" ? "was interrupted" : "failed"}`;

        await _parseErrorResponse({ message: msg }, dispatch, getState);
        return "";
      }

      // Started / InProgress => wait and poll again
      await delay(POLL_INTERVAL_MS, signal);
    }
  } finally {
    activePollProcessIds.delete(aiWaitingId);
  }
};

export const startAIImageGeneration = (lookAiPopupContext, signal) => {
  return async (dispatch, getState) => {
    const postData = await _preparePostData(lookAiPopupContext, getState(), dispatch);

    console.log("postData", postData);
    try {
      const rawResponse = await fetch(`${getBaseDomainUrl()}:7122/api/ImageProcessing/start-convert`, {
        method: 'POST',
        headers: newSwaggerHeaders,
        body: JSON.stringify(postData),
        signal,
      });

      console.log("rawResponse11", rawResponse);

      if (!rawResponse.ok) {
        await _parseErrorResponse(rawResponse, dispatch, getState); // ✅ centralized
        return "";
      }

      const data = await rawResponse.json();
      const processId = data?.processId;

      if (!processId) {
        await _parseErrorResponse(rawResponse, dispatch, getState); // ✅ centralized
        return "";
      }

      dispatch(setAIWaitingId(processId));

      attachResumeListenerOnce(dispatch, getState, signal);

      return await pollProgressUntilDone({ aiWaitingId: processId, signal, dispatch, getState });
    } catch (e) {
      // await new Promise(resolve => setTimeout(resolve, 5000)); // devtest // need to change api url to get here
      await _parseErrorResponse(e, dispatch, getState);
      return "";
    }
  }
}
